import { omit } from 'lodash';
import {
  DesignMap,
  DesignModule,
  DesignModuleNode,
  ExportValueOrVariable,
  InputExportConfig,
  InputObjectConfig,
  OasysExportConfig,
  oasysInputKeys,
  OasysObjectConfig,
  ObjectClass,
  TrustedResponseExportConfig,
  trustedResponseInputKeys,
  TrustedResponseObjectConfig,
  trustedResponseOutputKeys,
  ValueOrVariable,
  ValueOrVariableType
} from '@app/types';
import { filterNull } from '../array';
import { createUuid } from '../string';

type ExporterProps = {
  /** If @see item is a node, then this is the module it is in. */
  appModule?: DesignModule;
  item: DesignModuleNode;
  serializedMap: DesignMap;
};

type ExportedNode<TConfig = unknown> = (
  Omit<DesignModuleNode, 'props'>
  & {
    props: (
      Omit<DesignModuleNode['props'], 'config'>
      & { config: TConfig; }
    )
  }
);

/** Normalize a @see DesignObject or @see DesignModuleNode. */
export function exportNode(props: ExporterProps): ExportedNode<unknown> {
  props.item.props.config.disabledInFlow ??= false;

  switch (props.item._class) {
    case ObjectClass.input:
      return exportInputNode(props);
    case ObjectClass.oasys:
      return exportOasysNode(props);
    case ObjectClass.trustedResponse:
      return exportTrustedResponseNode(props);
  }

  return props.item;
};

function exportInputNode({
  item
}: ExporterProps): ExportedNode<InputExportConfig> {
  const {
    dtmfInterDigitTimeout,
    lengthOfSilence,
    maxIntentTimeout,
    ...config
  } = item.props.config as InputObjectConfig;

  const convertNumberOrVariable = (
    value: InputObjectConfig['dtmfInterDigitTimeout']
  ): ValueOrVariable<number | null> | number | null => {
    if (value === '') {
      return null;
    }

    return value;
  };

  return {
    ...item,
    props: {
      ...item.props,
      config: {
        disabledInFlow: false,
        ...config,
        ...filterNull({
          dtmfInterDigitTimeout: convertNumberOrVariable(dtmfInterDigitTimeout),
          lengthOfSilence: convertNumberOrVariable(lengthOfSilence),
          maxIntentTimeout: convertNumberOrVariable(maxIntentTimeout)
        })
      }
    }
  };
};

function exportOasysNode({
  item
}: ExporterProps): ExportedNode<OasysExportConfig> {
  const config = item.props.config as OasysObjectConfig;

  return {
    ...item,
    props: {
      ...item.props,
      config: {
        ...omit(config, oasysInputKeys),
        inputs: exportValueOrVariableInputs(config, oasysInputKeys)
      }
    }
  };
};

function exportTrustedResponseNode({
  item
}: ExporterProps): ExportedNode<TrustedResponseExportConfig> {
  const config = item.props.config as TrustedResponseObjectConfig;

  return {
    ...item,
    props: {
      ...item.props,
      config: {
        ...omit(config, [
          ...trustedResponseInputKeys,
          ...trustedResponseOutputKeys
        ]),
        inputs: exportValueOrVariableInputs(config, trustedResponseInputKeys),
        outputs: exportValueOrVariableInputs(config, trustedResponseOutputKeys)
      }
    }
  };
};

function exportValueOrVariableInputs<
  TKey extends string,
  TConfig extends Record<TKey, ValueOrVariable>
>(
  config: TConfig,
  valueOrVariableKeys: TKey[]
): ExportValueOrVariable[] {
  return filterNull(valueOrVariableKeys.map((key) => {
    const valueOrVariable = config[key];
    if (
      valueOrVariable.type === ValueOrVariableType.variable
      && !valueOrVariable.varName
    ) {
      return null;
    }

    return {
      ...valueOrVariable,
      argumentName: key,
      id: createUuid(),
      sensitiveData: false
    } satisfies ExportValueOrVariable;
  }));
}
